import type { Product } from "@/types";
import { applyFilters, DEFAULT_FILTERS } from "@/lib/shop-filters";
import { shouldSpreadCards, spreadCardsPerProduct } from "@/lib/product-sort";

/**
 * De eerste pagina van een productoverzicht (shop, categorie) voor de eerste HTML.
 *
 * Zonder JavaScript of browser-API's: de server bepaalt hiermee welke producten bij
 * het eerste renderen horen, met dezelfde sortering, kleurspreiding en paginagrootte
 * als de client (app/shop/ShopPageClient.tsx, app/product-category/[...slug]/
 * CategoryPageClient.tsx). Zo staat er na hydratie en na het binnenhalen van de
 * volledige lijst precies dezelfde eerste pagina: niets wisselt, niets verspringt.
 * Een wijziging van de volgorde in een van beide clients hoort hier mee te veranderen.
 */

// De vier echte nachtkleding-subcategorieën die de virtuele "nachtmode"-pagina
// bundelt (zelfde lijst als de mega-menu-opbouw in app/api/navigation/route.ts).
// Hier (en niet in category-page-data.ts) omdat ook de client hem nodig heeft en die
// geen serverbestand met firebase-admin mag binnenhalen.
export const NACHTMODE_TAKKEN = ["dames-nachtkleding", "heren-nachtkleding", "jongens-nachtkleding", "meisjes-nachtkleding"];

/** Aantal kaarten per pagina (zelfde waarde als PAGE_SIZE in beide clients). */
export const LIJST_PAGINA_GROOTTE = 30;

/**
 * Velden die een kaart, de zijbalk en de sortering nooit lezen, maar die per product
 * (en per kleur: colorOptions herhaalt ze) honderden bytes tot kilobytes kosten.
 * Alleen bedoeld voor de meegestuurde eerste pagina; de client haalt daarna de
 * volledige documenten op. Een lijst om weg te laten (geen lijst om te houden): een
 * nieuw veld op een product blijft zo gewoon beschikbaar voor de kaart.
 */
const PRODUCT_VELDEN_WEG = [
  "description", "shortDescription", "sizeChart", "sizeChartTemplateId",
  "upsellIds", "crossSellIds", "crossSells", "relatedProductIds",
  "metaTitle", "metaDescription", "tags", "altTexts",
  "deleteRequested", "deleteRequestedBy", "deleteRequestedAt", "updatedAt",
];
const KLEUR_VELDEN_WEG = [
  "description", "shortDescription", "sizeChart", "sizeChartTemplateId",
  "upsellIds", "crossSellIds", "crossSells", "relatedProductIds",
  "metaTitle", "metaDescription", "tags", "altTexts",
];

/** Kopie van het product zonder de velden die een overzichtskaart niet gebruikt. */
export function slankProductVoorLijst<T extends object>(product: T): T {
  const kopie: Record<string, unknown> = { ...(product as Record<string, unknown>) };
  for (const veld of PRODUCT_VELDEN_WEG) delete kopie[veld];
  if (Array.isArray(kopie.colorOptions)) {
    kopie.colorOptions = (kopie.colorOptions as Record<string, unknown>[]).map((optie) => {
      const o = { ...optie };
      for (const veld of KLEUR_VELDEN_WEG) delete o[veld];
      return o;
    });
  }
  return kopie as T;
}

/**
 * Zet Firestore-waarden (Timestamp met toMillis(), ook genest) om naar platte getallen,
 * zodat een document van een Server Component zonder serialisatiefout naar een
 * clientcomponent kan.
 */
export function maakSerialiseerbaar(waarde: unknown): unknown {
  if (waarde === null || waarde === undefined) return waarde;
  if (typeof waarde === "object") {
    const o = waarde as { toMillis?: () => number; constructor?: unknown };
    if (typeof o.toMillis === "function") return o.toMillis();
    if (Array.isArray(waarde)) return waarde.map(maakSerialiseerbaar);
    if (o.constructor === Object) {
      return Object.fromEntries(Object.entries(waarde as Record<string, unknown>).map(([k, v]) => [k, maakSerialiseerbaar(v)]));
    }
  }
  return waarde;
}

/**
 * De producten van een merkpagina (/merk/[slug]): op `brandSlug`, of op de merknaam als die als slug
 * (kleine letters, spaties naar streepjes) gelijk is. Dezelfde regel op de server (lib/brand-page-data.ts)
 * en in de terugval van de client (app/merk/[slug]/MerkPageClient.tsx).
 */
export function productenVanMerk<T extends { brandSlug?: string; brand?: string }>(producten: T[], slug: string): T[] {
  const norm = (s: unknown) => String(s || "").toLowerCase().trim();
  return producten.filter(
    (p) => norm(p.brandSlug) === norm(slug) || norm(p.brand).replace(/\s+/g, "-") === norm(slug)
  );
}

type KaartRegel = { key: string; product: Product };

/**
 * Het aantal kaarten (één per kleurvariant) van de lijst en de producten die de
 * eerste pagina vullen, in de volgorde waarin de client ze toont.
 *
 * `volgordeSlug`: de categorie waarvan de handmatige volgorde (categoryOrder) voorgaat
 * bij de standaardsortering; weglaten op /shop.
 */
export function eerstePaginaVanLijst(
  producten: Product[],
  opties: { volgordeSlug?: string; sort?: string } = {}
): { producten: Product[]; totaalKaarten: number } {
  const filters = { ...DEFAULT_FILTERS, sort: opties.sort ?? DEFAULT_FILTERS.sort };
  let zichtbaar = applyFilters(producten, filters);

  // Handmatige volgorde (F3) gaat voor bij "Bestverkocht"; zelfde regels als de categoriepagina.
  const slug = opties.volgordeSlug;
  if (slug && filters.sort === "relevance") {
    const metVolgorde: Product[] = [];
    const zonderVolgorde: Product[] = [];
    for (const p of zichtbaar) {
      if (p.categoryOrder?.[slug] != null) metVolgorde.push(p);
      else zonderVolgorde.push(p);
    }
    metVolgorde.sort((a, b) => (a.categoryOrder![slug] as number) - (b.categoryOrder![slug] as number));
    zichtbaar = [...metVolgorde, ...zonderVolgorde];
  }

  const kaarten = zichtbaar.flatMap((p): KaartRegel[] =>
    p.colorOptions && p.colorOptions.length > 0
      ? p.colorOptions.map((opt) => ({ key: `${p.id}-${opt.slug}`, product: p }))
      : [{ key: p.id, product: p }]
  );
  const geordend = shouldSpreadCards(filters.sort) ? spreadCardsPerProduct(kaarten) : kaarten;

  const ids = new Set<string>();
  const eerste: Product[] = [];
  for (const kaart of geordend.slice(0, LIJST_PAGINA_GROOTTE)) {
    if (!ids.has(kaart.product.id)) {
      ids.add(kaart.product.id);
      eerste.push(kaart.product);
    }
  }
  return { producten: eerste, totaalKaarten: geordend.length };
}
